"use client";

import { c as _c } from "react/compiler-runtime";
import { ContentBlockServer } from "@local/ui/src/ContentBlockServer";
import { HeaderMenuMobileButton } from "./Menu/MenuMobile";
import { SearchButton } from "./SearchButton";
import { CartButton } from "./CartButton";
import { useCartSelector, selectItemCount } from "@local/cart/src/cart";
import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { useCartLayout } from "../../../lib/useCartLayout";
import dynamic from "next/dynamic";
import { cn } from "@local/utils/src/cn";
import { useHydrated } from "@local/utils/src/useHydrated";
const SearchWrapper = dynamic(() => import("@local/search/src/components/Search/Wrapper").then(mod => mod.SearchWrapper), {
  ssr: false
});
interface TopNavigationBarClientProps {
  noNavigation: boolean;
  noCart: boolean;
  noSearch: boolean;
  showCta: boolean;
  market: string;
  className?: string;
  logo: ReactNode;
  navigation: ReactNode;
  cta: ReactNode;
  /**
   * Slot for an account entry point (e.g. @local/identity's AccountButton).
   * A slot rather than a direct import: @local/tracking and @local/webmcp
   * depend on @local/offer, so offer importing @local/identity (which uses
   * both) would create workspace cycles.
   */
  account?: ReactNode;
}
export const TopNavigationBarClient = t0 => {
  const $ = _c(35);
  const {
    noNavigation,
    noCart,
    noSearch,
    showCta,
    market,
    className,
    logo,
    navigation,
    cta,
    account
  } = t0;
  const itemCount = useCartSelector(market, "store", selectItemCount);
  const {
    toggleOpen: toggleCart,
    isOpen: isCartOpen
  } = useCartLayout();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchTriggerRef = useRef(null);
  const hydrated = useHydrated();
  const count = hydrated ? itemCount ?? 0 : 0;
  const hideNavigation = noNavigation || isSearchOpen;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setIsSearchOpen(false);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const closeSearch = t1;
  let t2;
  if ($[1] !== className) {
    t2 = cn("container px-4 xl:px-16", className);
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== hideNavigation) {
    t3 = !hideNavigation && <div className="flex-shrink-0 xl:hidden"><HeaderMenuMobileButton /></div>;
    $[3] = hideNavigation;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== logo) {
    t4 = <div className="flex items-center pt-1.5">{logo}</div>;
    $[5] = logo;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = !hideNavigation && navigation;
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <div className="flex h-full flex-1 flex-row items-center justify-center gap-6 xl:flex-none xl:justify-start">{t4}{t5}</div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== account || $[11] !== count || $[12] !== cta || $[13] !== hideNavigation || $[14] !== hydrated || $[15] !== isCartOpen || $[16] !== isSearchOpen || $[17] !== noCart || $[18] !== noSearch || $[19] !== showCta || $[20] !== toggleCart) {
    t7 = (!noSearch || !noCart || showCta || !!account) && <div className="flex flex-shrink-0 flex-row items-center gap-6 xl:gap-2">{!noSearch && !hideNavigation && <SearchButton ref={searchTriggerRef} onClick={() => setIsSearchOpen(true)} className={account ? "flex min-h-11 min-w-11 items-center justify-center p-0" : undefined} iconClassName={account ? "h-5 w-5" : undefined} />}{!isSearchOpen && account}{!noCart && !isSearchOpen && <CartButton count={count} ready={hydrated} onClick={() => toggleCart(!isCartOpen)} className={account ? "aspect-auto h-auto min-h-11 min-w-11 sm:mr-0 md:mr-0" : undefined} />}{cta}</div>;
    $[10] = account;
    $[11] = count;
    $[12] = cta;
    $[13] = hideNavigation;
    $[14] = hydrated;
    $[15] = isCartOpen;
    $[16] = isSearchOpen;
    $[17] = noCart;
    $[18] = noSearch;
    $[19] = showCta;
    $[20] = toggleCart;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t3 || $[23] !== t6 || $[24] !== t7) {
    t8 = <div className="flex w-full flex-row items-center justify-between gap-6 text-white xl:gap-4">{t3}{t6}{t7}</div>;
    $[22] = t3;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t2 || $[27] !== t8) {
    t9 = <ContentBlockServer fullWidthBgColor="bg-gray-950" className="flex h-[64px] flex-row items-center justify-center md:h-[60px]" contentClassName={t2}>{t8}</ContentBlockServer>;
    $[26] = t2;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== isSearchOpen || $[30] !== market) {
    t10 = <SearchWrapper isOpen={isSearchOpen} onClose={closeSearch} market={market} triggerRef={searchTriggerRef} />;
    $[29] = isSearchOpen;
    $[30] = market;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10 || $[33] !== t9) {
    t11 = <>{t9}{t10}</>;
    $[32] = t10;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
};